import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { handleSupabaseError } from '../lib/errors'
import { RunShare, RunWithDetails } from '../types'

export function useRunShares(runId: string) {
  return useQuery({
    queryKey: ['run_shares', runId],
    queryFn: async (): Promise<RunShare[]> => {
      const { data, error } = await supabase
        .from('run_shares')
        .select('*')
        .eq('run_id', runId)
        .order('created_at', { ascending: false })
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    enabled: !!runId
  })
}

export function useCreateRunShare() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (runId: string): Promise<RunShare> => {
      const { data, error } = await supabase
        .from('run_shares')
        .insert({ run_id: runId, is_public: true })
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['run_shares', data.run_id] })
    }
  })
}

export function useUpdateRunShare() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string
      updates: Partial<Pick<RunShare, 'is_public'>>
    }): Promise<RunShare> => {
      const { data, error } = await supabase
        .from('run_shares')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['run_shares', data.run_id] })
    }
  })
}

export function useDeleteRunShare() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      // Get the run_id first so we can invalidate the cache
      const { data: share } = await supabase
        .from('run_shares')
        .select('run_id')
        .eq('id', id)
        .single()
      
      const { error } = await supabase
        .from('run_shares')
        .delete()
        .eq('id', id)
      
      if (error) throw handleSupabaseError(error)
      return share!.run_id
    },
    onSuccess: (runId) => {
      queryClient.invalidateQueries({ queryKey: ['run_shares', runId] })
    }
  })
}

// For public access (no authentication required)
export function useSharedRun(slug: string) {
  return useQuery({
    queryKey: ['shared_run', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('run_shares')
        .select(`
          *,
          run:runs (
            *,
            segments (*),
            aid_stations (*),
            plan_items (*)
          )
        `)
        .eq('slug', slug)
        .eq('is_public', true)
        .single()
      
      if (error) throw handleSupabaseError(error)
      
      // Fetch calibration profile separately if it exists
      let calibrationProfile = null
      if (data.run.calibration_profile_id) {
        const { data: profileData } = await supabase
          .from('calibration_profiles')
          .select('*')
          .eq('id', data.run.calibration_profile_id)
          .single()
        
        calibrationProfile = profileData
      }
      
      // Transform the data to match our RunWithDetails type
      const transformedRun: RunWithDetails = {
        ...data.run,
        segments: data.run.segments || [],
        aid_stations: data.run.aid_stations || [],
        plan_items: data.run.plan_items || [],
        calibration_profile: calibrationProfile
      }
      
      return {
        ...data,
        run: transformedRun
      }
    },
    enabled: !!slug
  })
}